// pages/LandingPage.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../components/Footer"; // <--- Imported the reusable Footer
import "./LandingPage.css";

// --- CUSTOM HOOK FOR SCROLL ANIMATIONS ---
const useScrollReveal = (loading) => {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    setTimeout(() => {
      const elements = document.querySelectorAll(".reveal-on-scroll");
      elements.forEach((el) => observer.observe(el));
    }, 100);

    return () => observer.disconnect();
  }, [loading]);
};

// --- ICONS ---
const Icons = {
  chart: (
    <svg className="lp-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  inventory: (
    <svg className="lp-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  expiry: (
    <svg className="lp-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  billing: (
    <svg className="lp-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  parties: (
    <svg className="lp-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  reports: (
    <svg className="lp-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 2v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  check: (
    <svg className="lp-check" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '1.2rem', height: '1.2rem'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  star: (
    <svg className="lp-icon-star" viewBox="0 0 20 20" style={{width: '1.2rem', height: '1.2rem', fill:'currentColor', color:'#facc15'}}>
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  ),
  chevronDown: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '1rem', height: '1rem'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  menu: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '1.5rem', height: '1.5rem'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  close: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '1.5rem', height: '1.5rem'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  pill: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '100%', height: '100%'}}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
};

const getIcon = (iconName) => Icons[iconName] || Icons.chart;

export default function LandingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [theme, setTheme] = useState("dark");
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchLandingData();
  }, []);

  const fetchLandingData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/landing-page`
      );
      setData(response.data);
    } catch (error) {
      console.error("Error fetching landing page data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useScrollReveal(loading);

  const handleLogoClick = () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      navigate('/dashboard');
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="lp-loading">
        <div className="lp-spinner" />
        <p className="lp-loading-text" style={{marginTop:'1rem', color:'var(--lp-text-muted)'}}>Loading...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="lp-loading">
        <p style={{color: '#ef4444', fontWeight: 600}}>Failed to load page data</p>
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* --- NAVBAR --- */}
      <nav className="lp-nav">
        <div className="lp-container lp-nav-inner">
          
          <div 
            className="lp-brand" 
            onClick={handleLogoClick} 
            style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}} 
            title={localStorage.getItem('token') ? "Go to Dashboard" : "Scroll to Top"}
          >
            <img 
              src="./images/logoo.png" 
              alt="Pharmacy Logo" 
              style={{ width: '110px', height: 'auto', marginRight: '0.5rem' }} 
            />
            <span className="lp-brand-text" style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {data.logoText || "PharmaLedger"}
            </span>
          </div>

          <div style={{display:'flex', alignItems:'center'}}>
            {/* Theme Switcher */}
            <div className="lp-theme-toggles hidden-mobile">
              <button className={`lp-theme-btn ${theme === 'emerald' ? 'active' : ''}`} onClick={() => setTheme('emerald')} style={{background: '#059669'}} title="Emerald" />
              <button className={`lp-theme-btn ${theme === 'classic' ? 'active' : ''}`} onClick={() => setTheme('classic')} style={{background: '#2563eb'}} title="Classic" />
              <button className={`lp-theme-btn ${theme === 'modern' ? 'active' : ''}`} onClick={() => setTheme('modern')} style={{background: '#7c3aed'}} title="Modern" />
              <button className={`lp-theme-btn ${theme === 'dark' ? 'active' : ''}`} onClick={() => setTheme('dark')} style={{background: '#1e293b'}} title="Dark" />
            </div>

            {/* Desktop Auth */}
            <div className="hidden-mobile" style={{display:'flex', gap:'1rem', alignItems:'center'}}>
              <Link to="/login" style={{textDecoration:'none', color:'var(--lp-text-muted)', fontWeight:600}}>Log in</Link>
              <Link to="/register" className="lp-btn lp-btn-primary lp-btn-sm">Sign up</Link>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="hidden-desktop"
              style={{background:'transparent', border:'none', cursor:'pointer', color:'var(--lp-text-main)'}}
            >
              {mobileMenuOpen ? Icons.close : Icons.menu}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="hidden-desktop" style={{background: 'var(--lp-bg-surface)', borderTop: '1px solid var(--lp-border)', padding: '1rem'}}>
             <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="lp-btn lp-btn-secondary" style={{display:'block', width:'100%', marginBottom:'0.5rem', textAlign:'center'}}>Log in</Link>
             <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="lp-btn lp-btn-primary" style={{display:'block', width:'100%', textAlign:'center'}}>Sign up</Link>
             
             <div style={{marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid var(--lp-border)', display:'flex', justifyContent:'center', gap:'1rem'}}>
                <button onClick={() => setTheme('emerald')} style={{width:24, height:24, borderRadius:'50%', background:'#059669', border:'none'}} />
                <button onClick={() => setTheme('classic')} style={{width:24, height:24, borderRadius:'50%', background:'#2563eb', border:'none'}} />
                <button onClick={() => setTheme('modern')} style={{width:24, height:24, borderRadius:'50%', background:'#7c3aed', border:'none'}} />
                <button onClick={() => setTheme('dark')} style={{width:24, height:24, borderRadius:'50%', background:'#1e293b', border:'1px solid #ccc'}} />
             </div>
          </div>
        )}
      </nav>

      {/* --- HERO --- */}
      <section className="lp-hero">
        <div className="lp-container lp-hero-grid">
          <div className="reveal-on-scroll">
            <div className="lp-hero-badge">Smart ledger for pharmacies</div>

            <h1 className="lp-hero-title">
              {data.heroTitle?.split(" ").map((word, i, arr) =>
                i >= arr.length - 2 ? (
                  <span key={i} className="lp-text-gradient">{word} </span>
                ) : (
                  `${word} `
                )
              )}
            </h1>

            <p className="lp-hero-desc">{data.heroSubtitle}</p>

            <div className="lp-hero-actions">
              <Link to={data.heroButtonLink || "/register"} className="lp-btn lp-btn-primary lp-btn-lg">
                {data.heroButtonText || "Start free trial"}
              </Link>
              <a href="#features" className="lp-btn lp-btn-secondary lp-btn-lg">
                Explore features
              </a>
            </div>
          </div>

          {/* Right illustration */}
          <div className="reveal-on-scroll delay-200 hidden-mobile">
             <div className="lp-hero-graphic">
               <div className="lp-graphic-row">
                 <div>
                   <div style={{fontSize:'0.8rem', color:'var(--lp-text-muted)'}}>Today's Sales</div>
                   <div style={{fontSize:'1.5rem', fontWeight:'bold'}}>Rs45,230</div>
                 </div>
                 <div style={{color:'var(--lp-success)'}}>{Icons.check}</div>
               </div>
               <div className="lp-graphic-row">
                 <div>
                    <div style={{fontSize:'0.8rem', color:'var(--lp-text-muted)'}}>Expiring Soon</div>
                    <div style={{fontSize:'1.5rem', fontWeight:'bold', color:'var(--lp-danger)'}}>12 Items</div>
                 </div>
               </div>
             </div>
          </div>
        </div>
      </section>

      {/* --- STATISTICS --- */}
      {data.showStatistics && data.statistics?.length > 0 && (
        <section className="lp-stats">
          <div className="lp-container lp-stats-grid">
            {data.statistics.map((stat, index) => (
              <div key={index} className={`reveal-on-scroll delay-${(index + 1) * 100}`}>
                <div className="lp-stat-val">{stat.value}</div>
                <div className="lp-stat-lbl">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- FEATURES --- */}
      <section id="features" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-header reveal-on-scroll">
            <h2 className="lp-section-title">{data.featuresTitle}</h2>
            <p className="lp-section-sub">{data.featuresSubtitle}</p>
          </div>

          <div className="lp-feature-grid">
            {data.features?.map((feature, index) => (
              <div key={index} className={`lp-feature-card reveal-on-scroll delay-${(index % 3 + 1) * 100}`}>
                <div className="lp-feature-icon">
                  {getIcon(feature.icon)}
                </div>
                <h3 style={{fontSize:'1.25rem', marginBottom:'0.5rem', fontWeight:600}}>{feature.title}</h3>
                <p style={{color:'var(--lp-text-muted)'}}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- PRICING --- */}
      {data.showPricing && (
        <section id="pricing" className="lp-section" style={{background:'var(--lp-bg-subtle)'}}>
          <div className="lp-container">
            <div className="lp-section-header reveal-on-scroll">
              <h2 className="lp-section-title">{data.pricingTitle}</h2>
              <p className="lp-section-sub">{data.pricingSubtitle}</p>
            </div>

            <div className="lp-pricing-grid">
              {data.pricingPlans?.map((plan, index) => (
                <div
                  key={index}
                  className={`lp-pricing-card ${plan.isPopular ? "popular" : ""} reveal-on-scroll delay-${(index + 1) * 100}`}
                >
                  {plan.isPopular && (
                    <div className="lp-badge">Most Popular</div>
                  )}
                  <div style={{textAlign:'center'}}>
                    <h3 style={{fontSize:'1.3rem', fontWeight:600}}>{plan.name}</h3>
                    <p style={{color:'var(--lp-text-muted)', fontSize:'0.9rem'}}>{plan.description}</p>
                    <div className="lp-price">{plan.price}<span>{plan.period}</span></div>
                  </div>
                  
                  <ul className="lp-features-list">
                    {plan.features?.map((feature, i) => (
                      <li key={i}>
                        {Icons.check}
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={plan.buttonLink || "/register"}
                    className={`lp-btn ${plan.isPopular ? "lp-btn-primary" : "lp-btn-secondary"}`}
                    style={{width:'100%'}}
                  >
                    {plan.buttonText}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --- TESTIMONIALS --- */}
      {data.showTestimonials && data.testimonials?.length > 0 && (
        <section id="testimonials" className="lp-section">
          <div className="lp-container">
            <div className="lp-section-header reveal-on-scroll">
              <h2 className="lp-section-title">{data.testimonialsTitle}</h2>
            </div>

            <div className="lp-feature-grid">
              {data.testimonials.map((testimonial, index) => (
                <div key={index} className={`lp-feature-card reveal-on-scroll delay-${(index + 1) * 100}`}>
                  <div style={{display:'flex', gap:'0.2rem', marginBottom:'0.8rem'}}>
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <span key={i}>{Icons.star}</span>
                    ))}
                  </div>
                  <p style={{fontStyle:'italic', color:'var(--lp-text-muted)', marginBottom:'1.2rem'}}>
                    "{testimonial.content}"
                  </p>
                  <div style={{display:'flex', alignItems:'center', gap:'0.8rem'}}>
                    <div style={{width:'2.5rem', height:'2.5rem', borderRadius:'50%', background:'var(--lp-primary-dark)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700}}>
                        {testimonial.name?.charAt(0)}
                    </div>
                    <div>
                      <div style={{fontWeight:600}}>{testimonial.name}</div>
                      <div style={{fontSize:'0.85rem', color:'var(--lp-text-muted)'}}>{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --- FAQ --- */}
      {data.showFAQ && data.faqs?.length > 0 && (
        <section id="faq" className="lp-section" style={{background:'var(--lp-bg-subtle)'}}>
          <div className="lp-container" style={{maxWidth:'700px'}}>
            <div className="lp-section-header reveal-on-scroll">
              <h2 className="lp-section-title">{data.faqTitle}</h2>
            </div>

            <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
              {data.faqs.map((faq, index) => (
                <div
                  key={index}
                  className="reveal-on-scroll delay-100"
                  style={{background:'var(--lp-bg-surface)', borderRadius:'var(--lp-radius-md)', border:'1px solid var(--lp-border)', overflow:'hidden'}}
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    style={{width:'100%', padding:'1rem 1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', background:'transparent', border:'none', cursor:'pointer', textAlign:'left', color:'var(--lp-text-main)'}}
                  >
                    <span style={{fontWeight:600}}>{faq.question}</span>
                    <span style={{transform: openFAQ === index ? 'rotate(180deg)' : 'rotate(0)', transition:'transform 0.2s'}}>
                      {Icons.chevronDown}
                    </span>
                  </button>
                  {openFAQ === index && (
                    <div style={{padding:'0 1.5rem 1.5rem', color:'var(--lp-text-muted)', lineHeight:1.6}}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --- CTA --- */}
      <section className="lp-section">
        <div className="lp-container reveal-on-scroll">
           <div className="lp-cta-box">
              <h2>{data.ctaTitle}</h2>
              <p>{data.ctaSubtitle}</p>
              <Link to={data.ctaButtonLink || "/register"} className="lp-btn lp-btn-light lp-btn-lg">
                {data.ctaButtonText}
              </Link>
           </div>
        </div>
      </section>

      {/* --- SHARED FOOTER COMPONENT --- */}
      <Footer />
    </div>
  );
}